import { useAppSelector } from 'src/redux-file/hooks';
import { useProductImpact, useProductImpacts } from '../hooks';

export const emptyProductForm = {
  id: 0,
  name: '',
  description: '',
  development_flowcharts_description: '',
  related_documents_description: '',
  technical_documentation_description: '',
  manufacturer_name: '',
  importer_name: '',
  key_company_activities: '',
  regulatory_frameworks: '',
  product_category_rules: '',
  revision: '',
  lca_preparation_date: '',
  valid_until: '',
  production_country: {
    iso_code: '',
    name: '',
  },
  production_location: '',
  cpc_industry: {
    code: '',
    description: '',
  },
  functional_unit: '',
  data_collection_period: '',
  weight: null,
  weight_unit: '',
  dimensions: '',
  dimensions_unit: '',
  volume: null,
  volume_unit: '',
  production_process_description: '',
  assumptions_description: '',
  reference_service_life: '',
  cutoff_rules: '',
  additional_information: '',
  product_carbon_footprint: '',
  emissions_to_air_water_soil_noise: '',
  microplastics_nanoplastics_release: '',
  energy_water_resource_consumption: '',
  primary_energy_use: '',
  recovered_exported_energy: '',
  product_performance_factors_impact: '',
  deforestation_impact: '',
  recycled_materials_share: '',
  material_recovery_critical_raw_materials: '',
  renewable_materials_content: '',
  waste_generated: '',
  co2_sequestration_potential: '',
  ozone_depletion_potential: '',
  acidification_potential: '',
  eutrophication_potential: '',
  photochemical_ozone_creation_potential: '',
  lifecycle_boundaries: '',
  distribution_regions: [
    {
      iso_code: '',
      name: '',
    },
  ],
  materials: [
    {
      tempId: 0,
      material_name: '',
      percentage: 0,
      recycled_share_pct: 0,
      is_basic: true,
    },
    {
      tempId: 0,
      material_name: '',
      percentage: 0,
      recycled_share_pct: 0,
      is_basic: false,
    },
  ],
  goals: {
    intended_applications: '',
    study_reasons: '',
    target_audience: '',
    comissioner_name: '',
    external_experts_involved: true,
    external_experts_description: '',
    results_disclosed: false,
    assertions_disclosed: false,
    results_confidential: false,
    assertions_confidential: false,
    disclosure_description: '',
    comment: '',
  },
  nace_industries: [{ code: '', description: '' }],
  image_id: null,
  passport_qr_image_id: null,
  passport_qr_target_url: '',
  gtin_code: '',
  gpc_code: null,
  ecoinvent_version: null,
  ce_mark: false,
  model_batch_serial_number: '',
  expected_product_lifetime_years: '',
  repairability_information: '',
  availability_of_spare_parts: '',
  repair_disassembly_instructions_description: '',
  proper_use_instructions: '',
  delivery_time_of_spare_parts: '',
  affordability_of_spare_parts: '',
  compatibility_with_commonly_available_tools_and_spare_parts: '',
  recycling_possibilities: '',
  disassembly_instructions: '',
  epr_information_description: '',
  disposal_instructions: '',
  safe_easy_non_destructive_access_to_recyclable_components_materials_or_components_containing_hazardous_substances:
    '',
  material_composition_and_material_homogeneity: '',
  ability_to_sort_materials_while_maintaining_high_purity: '',
  ease_of_non_destructive_disassembly_and_reassembly: '',
  avoidance_of_technical_design_solutions_that_hinder_reuse_upgrade_repair_maintenance_refurbishment_remanufacturing_or_recycling_of_products_and_components:
    '',
  guaranteed_service_life: '',
  operating_life: '',
  mean_time_between_failures: '',
  actual_product_use_information: '',
  resistance_to_stress_or_aging_mechanisms: '',
  material_efficiency_lightweight_design_features: '',
  waste_reduction_in_product_design: '',
  hazardous_substances_level: '',
  recommended_fire_extinguishing_agents: '',
  safety_labels_and_symbols: '',
  safe_use_instructions: '',
  use_of_substances_of_concern_during_manufacturing_process: '',
  presence_of_substances_of_concern_in_product_as_substances_mixtures_or_components:
    '',
  presence_of_substances_of_concern_when_product_becomes_waste: '',
  impact_of_substances_of_concern_on_human_health_and_environment: '',
  amount_of_consumables_required_for_proper_operation_and_maintenance: '',
  characteristics_of_consumables: '',
  availability_of_consumables: '',
  performance_efficiency_of_consumables: '',
  technical_lifetime_of_consumables: '',
  reusability_of_consumables: '',
  repairability_of_consumables: '',
  remanufacturability_regenerability_of_consumables: '',
  product_modularity: '',
  use_of_standardised_components: '',
  component_material_coding_standards: '',
  number_and_complexity_of_repair_processes: '',
  need_for_specialised_tools: '',
  conditions_for_access_to_product_data: '',
  conditions_for_access_to_required_equipment_and_software: '',
  functional_performance_of_product: '',
  compatibility_with_other_products_or_systems: '',
  required_user_skills: '',
  access_to_testing_protocols_or_specialised_testing_equipment: '',
  warranty_availability_for_refurbished_or_remanufactured_products: '',
  access_conditions_to_intellectual_property_protected_technologies: '',
  product_type: '',
  production_date: null,
  warranty_period_conditions: '',
  suppliers: '',
  technical_specifications: '',
};

const weightResolver = (item: null | string | number) => {
  if (item === null) {
    return null;
  } else if (typeof item === 'number') {
    return item;
  } else if (typeof item === 'string') {
    return +item.replace(',', '.');
  }
};

export const sanitizedValues = (
  values: typeof emptyProductForm,
  organization: any
) => {
  const hasValue = (x: any) => x !== undefined && x !== null && x !== '';
  // sanitize object with regard to its pk, and map it to an object: {pk}
  const sanitizedSingle = <T>(item: T, pk_name: keyof T) => {
    return hasValue(item[pk_name]) ? { [pk_name]: item[pk_name] } : null;
  };
  const sanitizedList = <T>(items: T[], pk_name: keyof T) => {
    return items
      .map((x) => sanitizedSingle(x, pk_name))
      .filter((x) => x !== null);
  };
  return {
    ...values,
    organization_id: organization.id,
    weight: weightResolver(values.weight),
    materials: values.materials
      .filter((el) => hasValue(el.material_name))
      .map((el) => ({
        ...el,
        recycled_share_pct: el.recycled_share_pct ?? 0,
      })),
    nace_industries: sanitizedList(values.nace_industries, 'code'),
    distribution_regions: sanitizedList(
      values.distribution_regions,
      'iso_code'
    ),
    production_country: sanitizedSingle(values.production_country, 'iso_code'),
    cpc_industry: sanitizedSingle(values.cpc_industry, 'code'),
  };
};

export const useIsLcaPartialResults = (productId: number) => {
  const { selectedAreaOfImpact } = useAppSelector((state) => state.lcaDiagram);

  const { data: impactData } = useProductImpact(
    productId as number,
    selectedAreaOfImpact?.id
  );

  return Object.values(impactData?.emission_per_node || {}).some(
    (x) => x === null
  );
};

export const useIsLcaPartialResultsGlobal = (productId: number) => {
  const { data } = useProductImpacts(productId as number);
  return (data || []).some((item) =>
    Object.values(item.emission_per_node || {}).some((x) => x === null)
  );
};
